import { Icon } from '~/components';
import type {
  LegendColor,
  LegendName,
} from '~/modules/label-perf/types/label-perf';
import { formatNumberSuffix } from '~/modules/label-perf/utils/format';

export interface EnrichedDatum {
  seriesName: LegendName;
  rawValue: number;
  seriesColor: LegendColor;
  x: string | number;
  y: string | number;
  delta?: number;
  label?: string;
  date?: string;
}

interface ContentTooltipProps {
  datum?: EnrichedDatum;
  activePoints?: Array<{ datum: EnrichedDatum }>;
}

const ContentTooltip = ({ datum, activePoints }: ContentTooltipProps) => {
  const currentPoint = datum || (activePoints && activePoints[0]?.datum);

  if (!currentPoint) {
    return null;
  }

  const { date, y, seriesName, rawValue, seriesColor } = currentPoint;

  const displayDate = date || 'N/A';
  const displayPercentage =
    y !== undefined ? `${Number(y).toFixed(2)}%` : 'N/A';
  const displayRawValue =
    rawValue !== undefined ? formatNumberSuffix(rawValue) : '';
  const displaySeriesName = seriesName || 'N/A';

  const bulletColor = typeof seriesColor === 'string' ? seriesColor : '#FFFFFF';

  return (
    <div
      data-testid="content-tooltip-wrapper"
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Triangle/Caret */}
      <div
        style={{
          backgroundColor: '#232323',
          minWidth: 5,
          minHeight: 5,
          rotate: '45deg',
          clipPath: 'polygon(0 0,100% 100%,0 100%)',
          border: '0.75px solid #FFFFFF1F',
          marginRight: -3,
          zIndex: 10,
        }}
      />
      {/* Main Content Box */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: 4,
          width: 'fit-content',
          backgroundColor: '#232323',
          color: 'white',
          border: '0.75px solid #FFFFFF1F',
          borderRadius: 2,
        }}
      >
        {/* Date Row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '1px 3px',
            fontSize: 5,
            backgroundColor: '#FFFFFF0F',
            borderRadius: 1,
          }}
        >
          <Icon type="calendar-day" size="sm" />
          <div data-testid="tooltip-date-value" style={{ textWrap: 'nowrap' }}>
            {displayDate}
          </div>
        </div>

        {/* Series Data Row */}
        <div className="flex items-center" style={{ gap: 4, fontSize: 4 }}>
          <div
            data-testid="tooltip-bullet-point"
            style={{
              fontSize: 16,
              lineHeight: '10px',
              color: bulletColor,
            }}
            className="font-extrabold"
          >
            &middot; {/* Bullet point */}
          </div>
          <div
            data-testid="tooltip-series-name"
            style={{
              width: 28,
              borderRadius: 1,
              marginTop: 2.5,
            }}
            className="font-medium"
          >
            {displaySeriesName}
          </div>
          <div
            data-testid="tooltip-percentage"
            style={{ marginTop: 2.5 }}
            className="font-semibold"
          >
            {displayPercentage}
          </div>
          <div
            data-testid="tooltip-raw-value"
            style={{ marginTop: 2.5 }}
            className="text-subtle"
          >
            ({displayRawValue})
          </div>
        </div>
      </div>
    </div>
  );
};

ContentTooltip.displayName = 'ContentTooltip';

export { ContentTooltip };
